import { Location } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import * as momentJalaali from 'moment-jalaali';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ReportExportService } from '../../../report-export.service';
import { ReportContext, ReportDefinition } from '../../../report.models';
import { toEnglishDigits } from '../../../report.utils';
import { RouteFunctionReportApiService } from '../route-function-report-api.service';
import { RouteApiStatus, RouteOption, RouteSummaryColumn, RouteSummaryFilters, RouteSummaryRow, VehicleOption } from './route-summary-report.models';
import { SUMMARY_PAGE_SIZE, SUMMARY_PAGE_SIZES, summaryColumns, summarySearchIndex, summaryTotals, summaryValue } from './route-summary-report.utils';

@Component({ selector: 'app-route-summary-report', templateUrl: './route-summary-report.component.html' })
export class RouteSummaryReportComponent implements OnChanges, OnDestroy {
  @Input() context!: ReportContext;
  @Input() definition!: ReportDefinition;
  readonly form = new FormGroup({
    iRoute: new FormControl(-1), strUnitID: new FormControl(''),
    strSDate: new FormControl(''), strEDate: new FormControl(''), bSchedule: new FormControl(false),
  });
  routes: RouteOption[] = [];
  vehicles: VehicleOption[] = [];
  columns: RouteSummaryColumn[] = [];
  rows: RouteSummaryRow[] = [];
  filteredRows: RouteSummaryRow[] = [];
  pagedRows: RouteSummaryRow[] = [];
  totals = summaryTotals([]);
  searchText = '';
  currentPage = 1;
  pageSize = SUMMARY_PAGE_SIZE;
  readonly pageSizeOptions = SUMMARY_PAGE_SIZES;
  filtersOpen = true;
  loading = false;
  hasError = false;
  hasRequestedData = false;
  errorMessage = '';
  private version = 0;
  private failedStage: 'options' | 'report' = 'options';
  private loadedDates = '';
  private readonly formSubscription = this.form.valueChanges.subscribe(() => { this.currentPage = 1; this.refresh(); });

  constructor(
    private readonly api: RouteFunctionReportApiService,
    private readonly gurd: GurdServiceService,
    private readonly notify: NotificationService,
    private readonly other: OtherService,
    private readonly router: Router,
    private readonly location: Location,
    private readonly exporter: ReportExportService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['context'] && !changes['definition']) return;
    this.version++;
    this.loading = false;
    this.form.enable({ emitEvent: false });
    this.columns = summaryColumns(this.context.type);
    this.rows = []; this.searchText = ''; this.currentPage = 1;
    this.hasRequestedData = false; this.hasError = false; this.filtersOpen = true; this.loadedDates = '';
    this.form.reset({ iRoute: -1, strUnitID: '', strSDate: momentJalaali().subtract(1, 'day').format('jYYYY/jMM/jDD'), strEDate: momentJalaali().format('jYYYY/jMM/jDD'), bSchedule: false }, { emitEvent: false });
    this.refresh();
    void this.loadOptions();
  }
  ngOnDestroy(): void { this.version++; this.formSubscription.unsubscribe(); }
  private get credentials(): Record<string, unknown> {
    return { iSystemUser: this.gurd.decrypt(localStorage.getItem('ID')!), strSession: this.gurd.decrypt(localStorage.getItem('Session')!) };
  }
  get canSearch(): boolean { const value = this.form.getRawValue(); return !!value.strSDate && !!value.strEDate; }
  get filterCount(): number {
    const value = this.form.getRawValue();
    const hasRoute = value.iRoute !== null && value.iRoute !== undefined && String(value.iRoute) !== '' && String(value.iRoute) !== '-1';
    return [value.strSDate, value.strEDate, hasRoute, value.strUnitID, value.bSchedule].filter(Boolean).length;
  }
  get hasOptionalFilters(): boolean {
    const value = this.form.getRawValue();
    const hasRoute = value.iRoute !== null && value.iRoute !== undefined && String(value.iRoute) !== '' && String(value.iRoute) !== '-1';
    return hasRoute || !!value.strUnitID || !!value.bSchedule;
  }
  get hasSearch(): boolean { return !!this.searchText.trim(); }
  get printSectionId(): string { return `route-summary-print-${this.context.type}`; }
  get subtitle(): string { return this.loadedDates || this.dateSubtitle(); }
  get searchPlaceholder(): string { return `جستجو در جدول: ${this.definition.searchFields.join('، ')}...`; }
  get emptyTitle(): string { return this.hasSearch ? 'با این فیلترها نتیجه‌ای پیدا نشد!' : this.hasRequestedData ? 'اطلاعاتی برای این بازه وجود ندارد' : 'برای مشاهده گزارش، جستجو کنید'; }

  async loadOptions(): Promise<void> {
    if (this.loading) return;
    const version = this.begin('options');
    this.routes = []; this.vehicles = [];
    try {
      const [routes, vehicles] = await Promise.all([
        this.api.post<RouteOption[] | RouteApiStatus>('Route_List', this.credentials),
        this.api.post<VehicleOption[] | RouteApiStatus>('FleetYar_Car_List_New ', {
          ID: this.credentials.iSystemUser, strSession: this.credentials.strSession,
          strUnitID: this.gurd.decrypt(localStorage.getItem('UnitID')!), bOnlyHasGPS: false, bWithIcon: false,
        }),
      ]);
      if (version !== this.version) return;
      this.routes = this.validate(routes);
      this.vehicles = this.validate(vehicles);
    } catch (error) { if (version === this.version) this.fail(error); }
    finally { this.finish(version); }
  }
  async loadReport(): Promise<void> {
    if (this.loading || !this.canSearch) return;
    const value = this.form.getRawValue() as RouteSummaryFilters;
    const version = this.begin('report');
    try {
      const data = await this.api.post<RouteSummaryRow[] | RouteApiStatus>(this.definition.endpoint!, {
        ...this.credentials, ...value, strSDate: toEnglishDigits(value.strSDate), strEDate: toEnglishDigits(value.strEDate),
      });
      if (version !== this.version) return;
      this.rows = this.validate(data); this.hasRequestedData = true;
      this.loadedDates = this.dateSubtitle(); this.filtersOpen = false; this.currentPage = 1;
      this.refresh();
    } catch (error) { if (version === this.version) { this.rows = []; this.refresh(); this.fail(error); } }
    finally { this.finish(version); }
  }
  retry(): void { if (this.failedStage === 'options') void this.loadOptions(); else void this.loadReport(); }
  toggleFilters(): void { if (!this.loading) this.filtersOpen = !this.filtersOpen; }
  clearFilters(): void {
    if (this.loading) return;
    const today = momentJalaali().format('jYYYY/jMM/jDD');
    this.form.reset({ iRoute: -1, strUnitID: '', strSDate: today, strEDate: today, bSchedule: false }, { emitEvent: false });
    this.searchText = ''; this.rows = []; this.currentPage = 1;
    this.hasRequestedData = false; this.hasError = false; this.errorMessage = ''; this.loadedDates = '';
    this.filtersOpen = true; this.refresh();
  }
  goBack(): void { this.location.back(); }
  onSearchChange(): void { if (!this.loading) { this.currentPage = 1; this.refresh(); } }
  clearSearch(): void { if (!this.loading) { this.searchText = ''; this.onSearchChange(); } }
  onPageSizeChange(size: number): void { this.pageSize = size; this.currentPage = 1; this.refresh(); }
  goToPage(page: number): void { this.currentPage = page; this.refresh(); }
  cellValue(row: RouteSummaryRow, column: RouteSummaryColumn): string | number { return summaryValue(row, column); }
  exportExcel(): void {
    if (this.loading || this.hasError || !this.filteredRows.length) return;
    this.exporter.exportRows(this.definition.fileName, ['#', ...this.columns.map(column => column.title)],
      this.filteredRows.map((row, index) => [index + 1, ...this.columns.map(column => summaryValue(row, column))]));
  }
  private refresh(): void {
    const query = toEnglishDigits(this.searchText).trim().toLowerCase();
    this.filteredRows = query ? this.rows.filter(row => summarySearchIndex(row, this.columns).includes(query)) : this.rows;
    this.totals = summaryTotals(this.filteredRows);
    this.currentPage = Math.max(1, Math.min(this.currentPage, Math.ceil(this.filteredRows.length / this.pageSize) || 1));
    this.pagedRows = this.filteredRows.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);
  }
  private begin(stage: 'options' | 'report'): number {
    this.failedStage = stage; this.loading = true; this.hasError = false;
    this.form.disable({ emitEvent: false }); return ++this.version;
  }
  private finish(version: number): void {
    if (version !== this.version) return;
    this.loading = false; this.form.enable({ emitEvent: false });
  }
  private validate<T>(data: T[] | RouteApiStatus): T[] {
    const status = (Array.isArray(data) ? data[0] : data) as RouteApiStatus | undefined;
    if (status?.ISSessionExpired || status?.ISessionExpired) {
      this.other.removeLocalStorage(); void this.router.navigate(['login'], { queryParams: { report: this.definition.loginReport } });
      throw new Error('لطفا وارد سیستم شوید');
    }
    if (status?.bError) throw new Error(status.strError || 'دریافت اطلاعات با خطا مواجه شد');
    if (!Array.isArray(data)) throw new Error('پاسخ سرور معتبر نیست');
    return data;
  }
  private fail(error: unknown): void {
    this.hasError = true; this.errorMessage = error instanceof Error ? error.message : 'دریافت اطلاعات با خطا مواجه شد';
    this.notify.showError(this.errorMessage);
  }
  private dateSubtitle(): string { const value = this.form.getRawValue(); return `از تاریخ ${value.strSDate} الی ${value.strEDate}`; }
}
